import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DialogContentText } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';

import {
    IModuleType,
    DeleteModuleDocument,
    UpdateCourseModuleDocument,
    ModuleEditDocument,
} from 'graphql/types';
import {
    CourseModuleForm,
    ICourseModuleFormProps,
} from 'training/components/forms/CourseModuleForm';
import { IModuleUpdateDrawerProps } from 'training/types';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { useApolloError } from 'common/hooks/useApolloError';
import { dayHoursToSeconds } from 'common/utils/formatDate';
import { Loader } from 'common/components/Loader';
import { getModuleOffsetFormValue } from 'training/utils/moduleOffset';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { getUrl } from 'route/utils/getUrl';
import { isUUID } from 'common/utils/isUUID';
import { NotFound } from 'common/components/NotFound';
import { getImageId } from 'common/utils/image';

import { CourseModuleSelectDrawer } from './CourseModuleSelectDrawer';

export const CourseModuleUpdateDrawer = ({
    id,
    onClose,
}: IModuleUpdateDrawerProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [searchParams, setSearchParams] = useSearchParams();

    const routeQuery = useRouteQuery();
    const courseIdParam = routeQuery.get('course') || undefined;
    const [selectedCourseId, setSelectedCourseId] = useState(courseIdParam);

    const courseSelectDrawerOpen = useHashMatch('TRAINING_MODULE_SELECT');

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const { data, loading } = useQuery(ModuleEditDocument, {
        variables: { id, type: IModuleType.Course },
    });

    const [updateModule] = useMutation(UpdateCourseModuleDocument);
    const [deleteModule, { loading: loadingDelete }] =
        useMutation(DeleteModuleDocument);

    useEffect(() => {
        if (!courseIdParam) return;

        // Remove course search params, if the course id is set we can remove it
        const { course, ...newSearchParams } = Object.fromEntries(searchParams);
        setSearchParams(new URLSearchParams(newSearchParams));
    }, [courseIdParam, searchParams, setSearchParams]);

    const module = data?.module;

    if (loading) {
        return <Loader />;
    }

    if (!isUUID(id) || !module || module?.__typename !== 'CourseModule') {
        return <NotFound />;
    }

    const handleSubmit: ICourseModuleFormProps['onSubmit'] = async (values) => {
        const { image, offset, ...otherValues } = values;

        const moduleValues = {
            ...otherValues,
            imageId: getImageId(module.image, image),
            offset: {
                seconds: dayHoursToSeconds(offset?.days, offset?.hours),
                type: offset?.type,
            },
        };

        try {
            await updateModule({
                variables: { id, module: moduleValues },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.course.update'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteModule({
                variables: {
                    id,
                    type: IModuleType.Course,
                },
                update: (cache) => {
                    cache.evict({ id: `CourseModule:${id}` });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.course.delete'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const {
        title,
        description,
        image,
        weight,
        openOffset,
        course,
        moduleGroup: { id: moduleGroupId },
    } = module;

    const initialValues = {
        title,
        description,
        courseId: course.id,
        image: image || undefined,
        weight: weight,
        offset: getModuleOffsetFormValue(openOffset),
    };

    return (
        <>
            <PageTitle>{translate('updateCourseModule')}</PageTitle>

            <CourseModuleSelectDrawer
                edit
                open={courseSelectDrawerOpen}
                onClose={() => {
                    // Redirect to the pathname to go to the page without hashtag.
                    navigate(window.location.pathname);
                }}
                onSelect={(course) => {
                    // Redirect to the pathname to go to the page without hashtag.
                    setSelectedCourseId(course.id);
                    navigate(window.location.pathname);
                }}
            />

            {!loading && !!course && (
                <CourseModuleForm
                    edit
                    course={course}
                    courseId={selectedCourseId}
                    initialValues={initialValues}
                    moduleGroupId={moduleGroupId}
                    onClose={onClose}
                    onDelete={() => setShowDeleteAlert(true)}
                    onReplaceCourse={() => {
                        navigate(getUrl('TRAINING_MODULE_SELECT'));
                    }}
                    onSubmit={handleSubmit}
                />
            )}

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={loadingDelete}
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={loadingDelete}
                            variant="contained"
                            onClick={() => {
                                setShowDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteCourseModuleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteCourseModuleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
